import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, FileText, AlertCircle, CheckCircle, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { API } from "../../App";

const INDUSTRIES = [
  { value: "hospitality", label: "Hospitality" },
  { value: "trades", label: "Trades" },
  { value: "health_wellness", label: "Health & Wellness" },
  { value: "professional_services", label: "Professional Services" },
  { value: "retail", label: "Retail" },
  { value: "other", label: "Other" }
];

const LeadEngineImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importResult, setImportResult] = useState(null);

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      setErrors(["CSV must have a header row and at least one data row"]);
      return;
    }

    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    
    // Check required columns
    const requiredColumns = ['company_name', 'website_url', 'industry'];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    
    if (missingColumns.length > 0) {
      setErrors([`Missing required columns: ${missingColumns.join(', ')}`]);
      return;
    }

    const data = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Validate
      if (!row.company_name || !row.website_url || !row.industry) {
        parseErrors.push(`Row ${i + 1}: Missing required field`);
        continue;
      }

      // Validate industry
      const validIndustries = INDUSTRIES.map(i => i.value);
      if (!validIndustries.includes(row.industry.toLowerCase().replace(/[^a-z_]/g, '_'))) {
        parseErrors.push(`Row ${i + 1}: Invalid industry "${row.industry}"`);
        continue;
      }

      data.push({
        company_name: row.company_name,
        website_url: row.website_url,
        industry: row.industry.toLowerCase().replace(/[^a-z_]/g, '_'),
        contact_name: row.contact_name || null,
        contact_email: row.contact_email || null
      });
    }

    setParsedData(data);
    setErrors(parseErrors);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      parseCSV(event.target?.result);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error("No valid data to import");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/lead-engine/prospects/import`, {
        prospects: parsedData
      });
      setImportResult(response.data);
      toast.success(`Imported ${response.data.created} prospects`);
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  const removeRow = (index) => {
    setParsedData(parsedData.filter((_, i) => i !== index));
  };

  return (
    <AdminLayout title="Import Prospects">
      <div className="max-w-4xl">
        <Link to="/admin/lead-engine" className="inline-flex items-center gap-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Lead Engine
        </Link>

        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <h2 className="text-lg font-bold mb-6">Import Prospects from CSV</h2>

          {/* Instructions */}
          <div className="mb-6 p-4 bg-[#0E0E0E] border border-[#2E2E2E]">
            <h3 className="font-medium mb-2">CSV Format</h3>
            <p className="text-sm text-[#8A8A8A] mb-2">Required columns:</p>
            <code className="text-xs bg-[#2E2E2E] px-2 py-1 text-[#5F8FB8]">
              company_name, website_url, industry
            </code>
            <p className="text-sm text-[#8A8A8A] mt-2">Optional columns:</p>
            <code className="text-xs bg-[#2E2E2E] px-2 py-1 text-[#5F8FB8]">
              contact_name, contact_email
            </code>
            <p className="text-sm text-[#8A8A8A] mt-3">
              Valid industries: hospitality, trades, health_wellness, professional_services, retail, other
            </p>
          </div>

          {/* Upload */}
          {!importResult && (
            <>
              <div 
                className="border-2 border-dashed border-[#2E2E2E] p-8 text-center cursor-pointer hover:border-[#5F8FB8] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-[#5F8FB8]" />
                <p className="text-[#8A8A8A] mb-2">Click to upload CSV file</p>
                <p className="text-xs text-[#6B7280]">or drag and drop</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30">
                  <h4 className="font-medium text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Parse Errors
                  </h4>
                  <ul className="text-sm text-red-300 space-y-1">
                    {errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview */}
              {parsedData.length > 0 && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium">{parsedData.length} prospects ready to import</h3>
                    <button
                      onClick={handleImport}
                      disabled={loading}
                      className="btn-primary text-sm"
                    >
                      {loading ? "Importing..." : "Import All"}
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#2E2E2E] text-left text-xs text-[#8A8A8A] uppercase">
                          <th className="p-2">Company</th>
                          <th className="p-2">Website</th>
                          <th className="p-2">Industry</th>
                          <th className="p-2">Contact</th>
                          <th className="p-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.slice(0, 20).map((row, i) => (
                          <tr key={i} className="border-b border-[#2E2E2E]">
                            <td className="p-2">{row.company_name}</td>
                            <td className="p-2 text-[#8A8A8A]">{row.website_url}</td>
                            <td className="p-2 text-[#8A8A8A]">{row.industry}</td>
                            <td className="p-2 text-[#8A8A8A]">{row.contact_name || "—"}</td>
                            <td className="p-2">
                              <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-300">
                                <X className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedData.length > 20 && (
                      <p className="text-xs text-[#8A8A8A] mt-2">
                        Showing first 20 of {parsedData.length} rows
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Result */}
          {importResult && (
            <div className="p-6 bg-green-500/10 border border-green-500/30 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
              <h3 className="text-lg font-bold mb-2">Import Complete</h3>
              <p className="text-[#8A8A8A] mb-4">
                {importResult.created} prospects created, {importResult.skipped} duplicates skipped
              </p>
              <div className="flex justify-center gap-3">
                <Link to="/admin/lead-engine" className="btn-primary text-sm">
                  View Prospects
                </Link>
                <button 
                  onClick={() => {
                    setImportResult(null);
                    setParsedData([]);
                    setErrors([]);
                  }}
                  className="btn-secondary text-sm"
                >
                  Import More
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeadEngineImport;
